import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

const ClubConfirmContext = createContext(null);

const defaultOptions = {
    title: 'Confirmar',
    description: '',
    confirmationText: 'Confirmar',
    cancellationText: 'Cancelar',
    confirmationButtonProps: {},
    cancellationButtonProps: {},
};

export function ClubConfirmProvider({ children, defaultOptions: providerDefaults = {} }) {
    const mergedDefaults = useMemo(() => ({ ...defaultOptions, ...providerDefaults }), [providerDefaults]);
    const [dialog, setDialog] = useState(null);
    const resolverRef = useRef(null);

    const closeDialog = useCallback((action) => {
        const resolver = resolverRef.current;
        resolverRef.current = null;
        setDialog(null);

        if (action === 'confirm') {
            resolver?.resolve();
            return;
        }

        resolver?.reject(new Error('cancelled'));
    }, []);

    const confirm = useCallback(
        (options = {}) => {
            const config = { ...mergedDefaults, ...options };

            return new Promise((resolve, reject) => {
                resolverRef.current = { resolve, reject };
                setDialog({ ...config, open: true });
            });
        },
        [mergedDefaults],
    );

    const value = useMemo(() => ({ confirm }), [confirm]);

    return (
        <ClubConfirmContext.Provider value={value}>
            {children}
            <AlertDialog
                open={Boolean(dialog?.open)}
                onOpenChange={open => {
                    // Só cancela no dismiss externo; o botão Confirmar já resolve via closeDialog('confirm')
                    if (!open && resolverRef.current) closeDialog('cancel');
                }}
            >
                <AlertDialogContent size="default" className="sm:max-w-md">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{dialog?.title}</AlertDialogTitle>
                        {dialog?.description ? (
                            <AlertDialogDescription>{dialog.description}</AlertDialogDescription>
                        ) : null}
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel
                            onClick={() => closeDialog('cancel')}
                            {...(dialog?.cancellationButtonProps || {})}
                        >
                            {dialog?.cancellationText || mergedDefaults.cancellationText}
                        </AlertDialogCancel>
                        <Button
                            onClick={() => closeDialog('confirm')}
                            {...(dialog?.confirmationButtonProps || {})}
                        >
                            {dialog?.confirmationText || mergedDefaults.confirmationText}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </ClubConfirmContext.Provider>
    );
}

export function useClubConfirm() {
    const context = useContext(ClubConfirmContext);
    if (!context) {
        throw new Error('useClubConfirm must be used within ClubConfirmProvider');
    }
    return context.confirm;
}
